import { memo, useMemo, useRef } from 'react';

import { chartThemeTokens } from 'design-system';

import { Permission } from '@/entities/permissions';
import { usePermission } from '@/features/_shared/user';
import { deepMerge } from '@/shared/lib/array/deepMerge';

import ChartShell from '../ChartShell';
import { ChartProvider } from '../context/ChartContext';
import useChartFunctions, {
  getStandardWatermarkAttributes,
  getWatermarkConfig,
  mergeChartEvents
} from '../hooks/options/useChartFunctions';
import usePrefersReducedMotion from '../hooks/usePrefersReducedMotion';
import createRangeSelector from '../hooks/useRangeSelector';
import { createChartAppearance } from '../theme/chartAppearance';

import type { ChartEventsOptions } from 'highcharts';
import type Highcharts from 'highcharts/highstock';
import type { ExtendedOptions } from '../types/highcharts';

export type StaticChartConfig = Highcharts.Options | Record<string, unknown>;
export type StaticChartRangeSelector = boolean | Highcharts.RangeSelectorOptions;
export type StaticChartZooming = boolean | Highcharts.ChartZoomingOptions;

export type StaticChartProps = {
  applyWatermark?: boolean;
  chartConfig: StaticChartConfig;
  /** Overrides the GX default and `chartConfig.colors` palettes. */
  colors?: Highcharts.Options['colors'];
  label?: string;
  rangeSelector?: StaticChartRangeSelector;
  zoomable?: StaticChartZooming;
};

type WatermarkEventOptions = {
  events?: ChartEventsOptions;
  exportEvents?: ChartEventsOptions;
};

const previewAppearance = createChartAppearance(chartThemeTokens, 'preview');

const applyFixedChartAppearance = (options: ExtendedOptions): ExtendedOptions => ({
  ...options,
  chart: {
    ...options.chart,
    backgroundColor: previewAppearance.background,
    style: {
      ...options.chart?.style,
      color: previewAppearance.foreground,
      fontFamily: previewAppearance.fontFamily
    }
  }
});

const createPreviewXAxisOptions = (): Highcharts.XAxisOptions => ({
  labels: { style: { color: previewAppearance.foreground } },
  gridLineColor: previewAppearance.grid,
  lineColor: previewAppearance.grid,
  tickColor: previewAppearance.grid
});

const createPreviewYAxisOptions = (): Highcharts.YAxisOptions => ({
  title: { text: '' },
  labels: { style: { color: previewAppearance.foreground } },
  gridLineColor: previewAppearance.grid
});

const createPreviewColorAxisOptions = (): Highcharts.ColorAxisOptions => ({
  labels: { style: { color: previewAppearance.foreground } }
});

const applyPreviewAxisAppearance = (options: ExtendedOptions): ExtendedOptions => {
  options.xAxis = Array.isArray(options.xAxis)
    ? options.xAxis.map(axis => deepMerge(createPreviewXAxisOptions(), axis))
    : deepMerge(createPreviewXAxisOptions(), options.xAxis ?? {});
  options.yAxis = Array.isArray(options.yAxis)
    ? options.yAxis.map(axis => deepMerge(createPreviewYAxisOptions(), axis))
    : deepMerge(createPreviewYAxisOptions(), options.yAxis ?? {});
  if (options.colorAxis) {
    options.colorAxis = Array.isArray(options.colorAxis)
      ? options.colorAxis.map(axis => deepMerge(createPreviewColorAxisOptions(), axis))
      : deepMerge(createPreviewColorAxisOptions(), options.colorAxis);
  }
  return options;
};

const applyReducedMotion = (
  options: ExtendedOptions,
  prefersReducedMotion: boolean
): ExtendedOptions => {
  if (!prefersReducedMotion) return options;

  return {
    ...options,
    chart: { ...options.chart, animation: false },
    plotOptions: {
      ...options.plotOptions,
      series: { ...options.plotOptions?.series, animation: false }
    },
    tooltip: { ...options.tooltip, animation: false }
  };
};

const applyStaticChartOptions = ({
  options,
  events,
  exportEvents
}: {
  options: ExtendedOptions;
  events?: ChartEventsOptions;
  exportEvents?: ChartEventsOptions;
}): ExtendedOptions => ({
  ...options,
  chart: {
    ...options.chart,
    events: events ? mergeChartEvents(options.chart?.events, events) : options.chart?.events
  },
  exporting: {
    ...options.exporting,
    enabled: false,
    buttons: {
      ...options.exporting?.buttons,
      contextButton: {
        ...options.exporting?.buttons?.contextButton,
        enabled: false
      }
    },
    chartOptions: {
      ...options.exporting?.chartOptions,
      chart: {
        ...options.exporting?.chartOptions?.chart,
        events: exportEvents
          ? mergeChartEvents(options.exporting?.chartOptions?.chart?.events, exportEvents)
          : options.exporting?.chartOptions?.chart?.events
      }
    }
  },
  navigation: {
    ...options.navigation,
    buttonOptions: {
      ...options.navigation?.buttonOptions,
      enabled: false
    }
  }
});

const isSeriesVisible = (series: unknown): boolean =>
  !(
    typeof series === 'object' &&
    series !== null &&
    'visible' in series &&
    series.visible === false
  );

const getConfigSeries = (chartConfig: StaticChartConfig): unknown[] => {
  const series = 'series' in chartConfig ? chartConfig.series : undefined;
  return Array.isArray(series) ? series : [];
};

const getRangeSelectorSeries = (chartConfig: StaticChartConfig) =>
  getConfigSeries(chartConfig).flatMap(series => {
    if (
      typeof series !== 'object' ||
      series === null ||
      !('data' in series) ||
      !Array.isArray(series.data)
    ) {
      return [];
    }

    return [{ data: series.data }];
  });

const resolveRangeSelector = (
  rangeSelector: StaticChartRangeSelector | undefined,
  chartConfig: StaticChartConfig
): Highcharts.RangeSelectorOptions => {
  if (!rangeSelector) return { enabled: false };

  const defaults = createRangeSelector(previewAppearance, getRangeSelectorSeries(chartConfig));
  return rangeSelector === true ? defaults : deepMerge(defaults, rangeSelector);
};

const defaultZooming: Highcharts.ChartZoomingOptions = {
  type: 'x',
  pinchType: 'x',
  mouseWheel: false
};

const resolveZooming = (
  zoomable: StaticChartZooming,
  configuredZooming: Highcharts.ChartZoomingOptions | undefined
): Highcharts.ChartZoomingOptions => {
  if (zoomable === false) {
    return {
      type: undefined,
      pinchType: undefined,
      mouseWheel: false
    };
  }

  const enabledZooming = deepMerge(
    deepMerge<Highcharts.ChartZoomingOptions>({}, defaultZooming),
    configuredZooming ?? {}
  );
  return zoomable === true ? enabledZooming : deepMerge(enabledZooming, zoomable);
};

export const StaticChart = memo(
  ({
    applyWatermark = true,
    chartConfig,
    colors,
    label = 'Static data chart',
    rangeSelector,
    zoomable
  }: StaticChartProps) => {
    const { hasPermission } = usePermission();
    const prefersReducedMotion = usePrefersReducedMotion();
    const useSmallWatermark = hasPermission(Permission.UseSmallChartWatermark);
    const { createEventOptions } = useChartFunctions(previewAppearance, prefersReducedMotion);
    const watermarkImage = useRef<Highcharts.SVGElement | null>(null);
    const { aspectRatio } = getWatermarkConfig(previewAppearance.watermark);

    const watermarkEventOptions = useMemo<WatermarkEventOptions>(() => {
      if (!applyWatermark) return {};

      return createEventOptions(
        watermarkImage,
        chart =>
          getStandardWatermarkAttributes({
            chart,
            aspectRatio,
            useSmallWatermark
          }),
        previewAppearance.watermark
      );
    }, [applyWatermark, aspectRatio, createEventOptions, useSmallWatermark]);

    const options = useMemo<ExtendedOptions>(() => {
      const base: ExtendedOptions = {
        accessibility: { enabled: false },
        credits: { enabled: false },
        colors: [...previewAppearance.palette],
        rangeSelector: { enabled: false },
        stockTools: { gui: { enabled: false } },
        legend: { itemStyle: { color: previewAppearance.foreground } },
        tooltip: {
          backgroundColor: previewAppearance.tooltipBackground,
          borderColor: previewAppearance.border,
          style: { color: previewAppearance.tooltipForeground }
        },
        xAxis: createPreviewXAxisOptions(),
        yAxis: createPreviewYAxisOptions()
      };
      const themedOptions = applyPreviewAxisAppearance(deepMerge(base, chartConfig));
      const mergedOptions = applyFixedChartAppearance(themedOptions);
      if (colors !== undefined) {
        mergedOptions.colors = [...colors];
      }
      mergedOptions.rangeSelector = resolveRangeSelector(rangeSelector, chartConfig);
      if (zoomable !== undefined) {
        mergedOptions.chart = {
          ...mergedOptions.chart,
          zooming: resolveZooming(zoomable, mergedOptions.chart?.zooming)
        };
      }
      return applyReducedMotion(
        applyStaticChartOptions({
          options: mergedOptions,
          events: watermarkEventOptions.events,
          exportEvents: watermarkEventOptions.exportEvents
        }),
        prefersReducedMotion
      );
    }, [chartConfig, colors, prefersReducedMotion, rangeSelector, watermarkEventOptions, zoomable]);

    const summary = useMemo(() => {
      const series = getConfigSeries(chartConfig);
      return {
        kind: 'line' as const,
        label,
        totalSeries: series.length,
        visibleSeries: series.filter(isSeriesVisible).length
      };
    }, [chartConfig, label]);
    const presentationKey = `${applyWatermark}-${JSON.stringify(colors ?? null)}`;

    return (
      <ChartProvider>
        <div className='w-full min-w-0'>
          <ChartShell
            isStockChart={Boolean(rangeSelector)}
            chartId={presentationKey}
            options={options}
            summary={summary}
          />
        </div>
      </ChartProvider>
    );
  }
);

StaticChart.displayName = 'StaticChart';

export default StaticChart;
